import { useCallback, useEffect, useState } from "react";

import { DEFAULT_PAGE_SIZE } from "config/pagination";

// Reusable "fetch a paginated list → loading / error / rows" hook.
//
// `fetchFn` is called as fetchFn({ limit, offset }) and must resolve to
// { items, total }. Pass a STABLE reference (wrap it in useCallback in the
// caller) so the effect does not re-run on every render.
export default function usePaginatedResource(
  fetchFn,
  { pageSize: initialPageSize = DEFAULT_PAGE_SIZE } = {}
) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items, total: totalCount } = await fetchFn({
        limit: pageSize,
        offset: page * pageSize,
      });
      setRows(items ?? []);
      setTotal(totalCount ?? 0);
    } catch (err) {
      setError(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fetchFn, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  return { rows, total, page, pageSize, loading, error, setPage, setPageSize, refresh: load };
}
